import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from './AuthProvider';
import { useNotificationContext } from '../context/NotificationContext';
import OTPVerification from './OTPVerification';
import PaymentModal from './PaymentModal';
import '../styles/BookingModal.css';

interface BookingFormData {
  service: string;
  mode: string;
  date: string;
  time: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  description: string;
}

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose }) => {
  const { user } = useContext(AuthContext) || { user: null };
  const { showSuccess, showError, showInfo } = useNotificationContext();
  const [currentStep, setCurrentStep] = useState(1);
  const [showBookingOTP, setShowBookingOTP] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [bookingId, setBookingId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<BookingFormData>({
    service: '',
    mode: '',
    date: '',
    time: '',
    name: '',
    phone: '',
    email: '',
    address: '',
    description: ''
  });

  // Pre-fill form if user is logged in
  useEffect(() => {
    if (user && isOpen) {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      setFormData(prev => ({
        ...prev,
        name: fullName,
        phone: user.phone || '',
        email: user.email || '',
        address: user.address || ''
      }));
    }
  }, [user, isOpen]);

  const resetForm = () => {
    setCurrentStep(1);
    setShowBookingOTP(false);
    setShowPaymentModal(false);
    setBookingId(null);
    setIsLoading(false);
    setFormData({
      service: '',
      mode: '',
      date: '',
      time: '',
      name: '',
      phone: '',
      email: '',
      address: '',
      description: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const nextStep = () => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const bookingData = {
        service_type: formData.service,
        consultation_type: formData.mode === 'online' ? 'virtual' : 'doorstep',
        date: formData.date,
        time: formData.time,
        location: formData.mode === 'offline' ? formData.address : null,
        case_description: formData.description || null,
        amount: 2000.00
      };

      let endpoint, requestData;

      if (user) {
        // Logged-in user booking
        endpoint = 'http://localhost:5000/api/v1/bookings/';
        requestData = bookingData;
      } else {
        // Guest user booking
        endpoint = 'http://localhost:5000/api/v1/bookings/guest';
        requestData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          ...bookingData
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user && { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` })
        },
        body: JSON.stringify(requestData),
      });

      const result = await response.json();

      if (result.success) {
        if (result.data.requiresOTPVerification) {
          // Guest user - show OTP verification
          setBookingId(result.data.bookingId);
          setShowBookingOTP(true);
        } else {
          // Logged-in user - show payment modal
          setBookingId(result.data.bookingId);
          setShowPaymentModal(true);
        }
      } else {
        showError(result.message || 'Failed to create booking. Please try again.');
      }
    } catch (error) {
      console.error('Booking error:', error);
      showError('Failed to create booking. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookingOTPSuccess = () => {
    // After OTP verification, show payment modal
    setShowBookingOTP(false);
    if (bookingId) {
      setShowPaymentModal(true);
    }
  };

  const handlePaymentSuccess = () => {
    showSuccess('Payment successful! Your consultation is confirmed.');
    setShowPaymentModal(false);
    handleClose();
  };

  const handleResendBookingOTP = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/v1/auth/resend-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: formData.phone,
          type: 'booking'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        showSuccess('OTP sent successfully!');
      } else {
        showError(result.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      showError('Failed to resend OTP. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="close-btn" onClick={handleClose}>×</button>
        <h2>Book Your Legal Consultation</h2>
        <div className="progress-bar">
          <div className="progress" style={{ width: `${(currentStep / 5) * 100}%` }}></div>
        </div>
        
        {currentStep === 1 && (
          <div className="form-step">
            <h3>Select Service Type</h3>
            <div className="service-options">
              {[
                { id: 'civil', name: 'Civil Law', icon: 'fas fa-gavel' },
                { id: 'criminal', name: 'Criminal Law', icon: 'fas fa-shield-alt' },
                { id: 'matrimonial', name: 'Matrimonial Law', icon: 'fas fa-heart' },
                { id: 'arbitration', name: 'Arbitration Law', icon: 'fas fa-handshake' },
                { id: 'mediation', name: 'Mediation Law', icon: 'fas fa-users' },
                { id: 'negotiable', name: 'Negotiable Law', icon: 'fas fa-file-contract' }
              ].map((service) => (
                <label key={service.id} className="service-option">
                  <input
                    type="radio"
                    name="service"
                    value={service.id}
                    checked={formData.service === service.id}
                    onChange={handleInputChange}
                  />
                  <div className="service-icon">
                    <i className={service.icon}></i>
                  </div>
                  <div className="service-info">
                    <span className="service-name">{service.name}</span>
                    <span className="service-price">₹2000</span>
                  </div>
                </label>
              ))}
            </div>
            <button type="button" className="btn btn-primary" onClick={nextStep} disabled={!formData.service}>
              Next
            </button>
          </div>
        )}

        {currentStep === 2 && (
          <div className="form-step">
            <h3>Select Consultation Mode</h3>
            <div className="consultation-options">
              <label className="consultation-option">
                <input
                  type="radio"
                  name="mode"
                  value="online"
                  checked={formData.mode === 'online'}
                  onChange={handleInputChange}
                />
                <span><i className="fas fa-video"></i> Online Consultation</span>
              </label>
              <label className="consultation-option">
                <input
                  type="radio"
                  name="mode"
                  value="offline"
                  checked={formData.mode === 'offline'}
                  onChange={handleInputChange}
                />
                <span><i className="fas fa-home"></i> Offline (Doorstep)</span>
              </label>
            </div>
            <div className="form-buttons">
              <button type="button" className="btn btn-secondary" onClick={prevStep}>Previous</button>
              <button type="button" className="btn btn-primary" onClick={nextStep} disabled={!formData.mode}>
                Next
              </button>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="form-step">
            <h3>Select Date & Time</h3>
            <div className="date-time-container">
              <div className="form-group">
                <label><i className="fas fa-calendar-alt"></i> Preferred Date</label>
                <div className="date-picker-container">
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    className="date-input"
                    required
                  />
                  <div className="quick-date-options">
                    <button 
                      type="button" 
                      className="quick-date-btn"
                      onClick={() => setFormData(prev => ({ ...prev, date: new Date().toISOString().split('T')[0] }))}
                    >
                      Today
                    </button>
                    <button 
                      type="button" 
                      className="quick-date-btn"
                      onClick={() => setFormData(prev => ({ ...prev, date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] }))}
                    >
                      Tomorrow
                    </button>
                    <button 
                      type="button" 
                      className="quick-date-btn"
                      onClick={() => setFormData(prev => ({ ...prev, date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] }))}
                    >
                      Next Week
                    </button>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label><i className="fas fa-clock"></i> Preferred Time</label>
                <div className="time-slots">
                  {[
                    { value: "09:00", label: "9:00 AM", available: true },
                    { value: "10:00", label: "10:00 AM", available: true },
                    { value: "11:00", label: "11:00 AM", available: false },
                    { value: "14:00", label: "2:00 PM", available: true },
                    { value: "15:00", label: "3:00 PM", available: true },
                    { value: "16:00", label: "4:00 PM", available: false },
                    { value: "17:00", label: "5:00 PM", available: true }
                  ].map((slot) => (
                    <label key={slot.value} className={`time-slot ${!slot.available ? 'unavailable' : ''}`}>
                      <input
                        type="radio"
                        name="time"
                        value={slot.value}
                        checked={formData.time === slot.value}
                        onChange={handleInputChange}
                        disabled={!slot.available}
                      />
                      <span>{slot.label}</span>
                      {!slot.available && <small>Booked</small>}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="form-buttons">
              <button type="button" className="btn btn-secondary" onClick={prevStep}>Previous</button>
              <button type="button" className="btn btn-primary" onClick={nextStep} disabled={!formData.date || !formData.time}>
                Next
              </button>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="form-step">
            <h3>Your Details</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group input-field">
                <label><i className="fas fa-user"></i> Full Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <i className="input-icon fas fa-user"></i>
              </div>
              <div className="form-group input-field">
                <label><i className="fas fa-phone"></i> Mobile Number</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Enter 10-digit mobile number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  pattern="[0-9]{10}"
                  required
                />
                <i className="input-icon fas fa-phone"></i>
              </div>
              <div className="form-group input-field">
                <label><i className="fas fa-envelope"></i> Email Address</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <i className="input-icon fas fa-envelope"></i>
              </div>
              <div className="form-group textarea-field">
                <label><i className="fas fa-map-marker-alt"></i> Address</label>
                <textarea
                  name="address"
                  placeholder="Enter your complete address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={3}
                  required
                />
                <i className="input-icon fas fa-map-marker-alt"></i>
              </div>
              <div className="form-group textarea-field">
                <label><i className="fas fa-file-alt"></i> Case Description (Optional)</label>
                <textarea
                  name="description"
                  placeholder="Brief description of your legal matter to help us prepare better"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                />
                <i className="input-icon fas fa-file-alt"></i>
              </div>
              <div className="form-buttons">
                <button type="button" className="btn btn-secondary" onClick={prevStep}>Previous</button>
                <button 
                  type="submit" 
                  className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading || !formData.name || !formData.phone || !formData.email || !formData.address}
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {user ? 'Booking...' : 'Sending OTP...'}
                    </>
                  ) : (
                    user ? 'Confirm Booking' : 'Send OTP'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Booking OTP Verification Modal */}
        <OTPVerification
          isOpen={showBookingOTP}
          onClose={() => setShowBookingOTP(false)}
          phone={formData.phone}
          onVerificationSuccess={handleBookingOTPSuccess}
          onResendOTP={handleResendBookingOTP}
          type="booking"
          bookingId={bookingId}
        />

        {/* Payment Modal */}
        {bookingId && (
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onPaymentSuccess={handlePaymentSuccess}
            bookingDetails={{
              bookingId: bookingId,
              amount: 2000,
              customerName: formData.name,
              customerEmail: formData.email,
              customerPhone: formData.phone,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default BookingModal;